import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';

import {
  accessLocalStorage,
  getCarrierById,
  createCarrier,
  updateCarrier,
  maskCnpj,
} from '../../../services';
import Loading from '../../Loading/LoadingModule';
import {
  CARRIER_MESSAGES,
  buildCarrierPayload,
  carrierFormError,
  resultErrorMessage,
} from './carrierScreen';

import '../../../libs/bulma.min.css';
import './Carriers.css';

const LIST_PATH = '/config/carriers';
const REDIRECT_DELAY_MS = 1800;
const CNPJ_MASK_LENGTH = 18;
const NAME_MAX_LENGTH = 100;
const NOT_FOUND_STATUS = 404;

const readSession = () => accessLocalStorage.getUserLocalStorage();

const sessionTenant = () => {
  const session = readSession();
  return session && session.user ? session.user.idtenant : null;
};

function CarriersForm() {
  const history = useHistory();
  const { carrierId } = useParams();
  const isEdit = carrierId !== undefined;

  const [values, setValues] = useState({ name: '', cnpj: '' });
  const [loading, setLoading] = useState(isEdit);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const redirectTimer = useRef(null);

  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  useEffect(() => {
    if (!readSession()) {
      history.push('/');
      return undefined;
    }
    if (!isEdit) return undefined;

    let active = true;
    getCarrierById(carrierId, sessionTenant()).then((result) => {
      if (!active) return;
      if (result.success && result.data) {
        setValues({ name: result.data.name, cnpj: maskCnpj(result.data.cnpj) });
      } else if (result.status === NOT_FOUND_STATUS) {
        setNotFound(true);
      } else {
        setError(resultErrorMessage(result, CARRIER_MESSAGES.loadFailed));
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [history, isEdit, carrierId]);

  const handleNameChange = (event) => {
    const { value } = event.target;
    setValues((current) => ({ ...current, name: value }));
  };

  const handleCnpjChange = (event) => {
    const masked = maskCnpj(event.target.value);
    setValues((current) => ({ ...current, cnpj: masked }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (savingRef.current) return;

    const validationError = carrierFormError(values);
    if (validationError) {
      setError(validationError);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setError(null);

    const payload = buildCarrierPayload(values, sessionTenant());
    const result = isEdit ? await updateCarrier(carrierId, payload) : await createCarrier(payload);

    if (!result.success) {
      savingRef.current = false;
      setSaving(false);
      setError(resultErrorMessage(result, CARRIER_MESSAGES.saveFailed));
      return;
    }

    setSuccess(isEdit ? CARRIER_MESSAGES.updated : CARRIER_MESSAGES.created);
    redirectTimer.current = setTimeout(() => history.push(LIST_PATH), REDIRECT_DELAY_MS);
  };

  const handleCancel = () => history.push(LIST_PATH);

  if (loading) return <Loading />;

  const title = isEdit ? CARRIER_MESSAGES.editTitle : CARRIER_MESSAGES.newTitle;

  if (notFound) {
    return (
      <div className="carriers-container">
        <h1 className="title is-4 carriers-title">{title}</h1>
        <div className="notification is-danger is-light">{CARRIER_MESSAGES.notFound}</div>
        <Link to={LIST_PATH}>{CARRIER_MESSAGES.backToList}</Link>
      </div>
    );
  }

  return (
    <div className="carriers-container">
      <div className="carriers-header">
        <h1 className="title is-4 carriers-title">{title}</h1>
      </div>

      {success && <div className="notification is-success is-light">{success}</div>}
      {error && <div className="notification is-danger is-light">{error}</div>}

      <form className="carriers-form" onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label className="label" htmlFor="carrier-name">{CARRIER_MESSAGES.nameLabel}</label>
          <div className="control">
            <input
              id="carrier-name"
              className="input"
              type="text"
              name="name"
              maxLength={NAME_MAX_LENGTH}
              value={values.name}
              onChange={handleNameChange}
              disabled={saving}
            />
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="carrier-cnpj">{CARRIER_MESSAGES.cnpjLabel}</label>
          <div className="control">
            <input
              id="carrier-cnpj"
              className="input"
              type="text"
              name="cnpj"
              inputMode="numeric"
              placeholder="00.000.000/0000-00"
              maxLength={CNPJ_MASK_LENGTH}
              value={values.cnpj}
              onChange={handleCnpjChange}
              disabled={saving}
            />
          </div>
        </div>

        <div className="field is-grouped">
          <div className="control">
            <button type="submit" className={`button is-primary${saving && !success ? ' is-loading' : ''}`} disabled={saving}>
              {CARRIER_MESSAGES.saveButton}
            </button>
          </div>
          <div className="control">
            <button type="button" className="button is-light" onClick={handleCancel}>
              {CARRIER_MESSAGES.cancelButton}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CarriersForm;
